import { SagaIterator, Task } from 'redux-saga';
import { all, call, fork, put, select, takeEvery, delay, spawn, race, cancelled } from 'redux-saga/effects';
import { GetBaseUrl } from 'src/domains/global/selectors';
import { GetApplicationState, GetInterrupt } from './selectors';
import { httpGet, httpPost } from 'src/utils';
import { Response } from 'src/domains/global/types';
import moment from 'moment';
import * as constants from './constants';
import { getPhase, getTimedoutPhases } from './utils';

import {
    startFetchApp,
    successFetchApp,
    checkPending,
    setPhaseStatus,
    startPinging,
    successPing,
    failPing,
    pingingTimeout,
    startCreate,
    startCompile,
    startInit,
    startBuildBackend,
    startStart,
    startLaunch,
    failureHeartbeat,
    successHeartbeat,
    optimisticStartAction,
    doStartPinging,
    // startBuild,
    // endBuild
} from './actions';

import {
    StartFetchAction,
    SuccessFetchAction,
    CheckPendingAction,
    StartCreateAction,
    StartDestroyAction,
    StartCompileAction,
    StartInitAction,
    StartBuildBackendAction,
    StartStartAction,
    StartStopAction,
    StartPingingAction,
    DoStartPingingAction,
    StartBuildAction,
    StartLaunchAction,
    HeartbeatStartAction,
    HeartbeatStopAction,
    ApplicationImpl,
    ApplicationState,
    AppPhase,
    Milestone,
    MilestoneDetail,
    ALL_MILESTONES_ORDERED
} from './types';

import {
    reset as explorerReset,
    setEntity as explorerSetEntity,
    startFetch as explorerStartFetch
} from 'src/domains/explorer/actions'

import _ from 'lodash';

const PING_INTERVAL = 5 * 1000;
const PING_HEARTBEAT_INTERVAL = 10 * 1000;
export const PING_TIMEOUT = 120 * 1000;

// not all milestones are here, this is intentional.
// timeouts are in seconds

// Base timeouts are for isolated milestones (without migration).
const TIMEOUTS: Map<Milestone, number> = new Map();
TIMEOUTS.set(Milestone.Src, 30);
TIMEOUTS.set(Milestone.Bin, 60);
TIMEOUTS.set(Milestone.Db, 60);
TIMEOUTS.set(Milestone.Backend, 360);
TIMEOUTS.set(Milestone.Running, 120);
TIMEOUTS.set(Milestone.Stopped, 120);
TIMEOUTS.set(Milestone.Erased, 60);

// intervals are in milliseconds (* 1000)
const INTERVALS: Map<Milestone, number> = new Map();
INTERVALS.set(Milestone.Src, 1 * 1000);
INTERVALS.set(Milestone.Bin, 3 * 1000);
INTERVALS.set(Milestone.Db, 3 * 1000);
INTERVALS.set(Milestone.Backend, 5 * 1000);
INTERVALS.set(Milestone.Running, 5 * 1000);
INTERVALS.set(Milestone.Stopped, 3 * 1000);
INTERVALS.set(Milestone.Erased, 1 * 1000);


let pinger: Task | null = null;

function* startWatchers(): SagaIterator {
    yield takeEvery(constants.START_FETCH_APP, startFetchWorker)
    yield takeEvery(constants.SUCCESS_FETCH_APP, successFetchWorker)
    yield takeEvery(constants.CHECK_PENDING, checkPendingWorker)
    yield takeEvery(constants.START_CREATE, startCreateWorker)
    yield takeEvery(constants.START_COMPILE, startCompileWorker)
    yield takeEvery(constants.START_BUILD_BACKEND, startBuildBackendWorker)
    yield takeEvery(constants.START_INIT, startInitWorker)
    yield takeEvery(constants.START_START, startStartWorker)
    yield takeEvery(constants.START_STOP, startStopWorker)
    yield takeEvery(constants.START_DESTROY, startDestroyWorker)
    yield takeEvery(constants.START_PINGING, startPingingWorker)
    yield takeEvery(constants.START_BUILD, startBuildWorker)
    yield takeEvery(constants.LAUNCH_START, startLaunchWorker)
    yield takeEvery(constants.HEARTBEAT_START, startHeartbeatWorker)
    yield takeEvery(constants.HEARTBEAT_STOP, stopHeartbeatWorker)
}

// Fetch
function* startFetchWorker(payload: StartFetchAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload
    const url = `${base}/api/app/${id}`

    try {
        let response: Response<any> = yield call(httpGet, url)
        const application = new ApplicationImpl(response.body);
        yield put(successFetchApp(application));
        yield put(checkPending(application));
        // yield put(explorerReset({ id: id.toString(), name: 'app' }));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Fetch, { timedout: true }));
    }
}

function* successFetchWorker(payload: SuccessFetchAction) {
    let base: string = yield select(GetBaseUrl)
    const { application } = payload

    // yield put(explorerSetEntity({ name: "app", id: application.id!.toString() }, application.getEntity()))
    yield put(explorerStartFetch({ name: "app", id: application.id!.toString() }, true, true));
}

function* checkPendingWorker(payload: CheckPendingAction) {
    const { application } = payload;
    const app: ApplicationState = yield select(GetApplicationState);
    const timedoutPhases = getTimedoutPhases(app);
    const pending: Milestone[] = application.getPendingMilestones().filter(milestone => !timedoutPhases.includes(getPhase(milestone)));
    const migration = application.migration;

    let milestone: Milestone | undefined;
    let timeout: number | undefined;
    let interval: number | undefined;
    let order: number = ALL_MILESTONES_ORDERED.length;

    for (milestone of pending) {
        if (pending.length > 1 && milestone == migration) {
            continue;
        }

        timeout = TIMEOUTS.get(milestone);

        const startedAt = application.getMoment(milestone, MilestoneDetail.Started);
        const now = moment();
        const diff = now.diff(startedAt, 'seconds');
        const timedout = diff > timeout!;

        yield put(setPhaseStatus(getPhase(milestone), { timedout }));

        if (!timedout && (ALL_MILESTONES_ORDERED.indexOf(milestone) < order)) {
            interval = INTERVALS.get(milestone);
        }
    }

    const interrupt: boolean = yield select(GetInterrupt)
    if (interrupt) {
        return;
    }

    // If there is a migration, and only one pending milestone which is the migration, use migration timeouts/intervals
    if (interval == null && migration != null) {
        interval = INTERVALS.get(migration);
    }

    if (interval != null && pending.length > 0) {
        yield delay(interval!);
        yield startFetchWorker(startFetchApp(application.id!));
    }
}

// Build All
function* startBuildWorker(payload: StartBuildAction) {
    let app: ApplicationState = yield select(GetApplicationState);
    const { id } = payload;
    let milestone: Milestone = Milestone.Src;

    if (app.application?.isAvail([milestone])) {
        try {
            yield startCreateWorker(startCreate(id));
            do {
                yield delay(500);
                app = yield select(GetApplicationState);
            } while (!app.application?.isSuccess([milestone]) && !app.application?.isError([milestone]))
        } catch (err: any) {
            yield put(setPhaseStatus(getPhase(milestone), { timedout: true }));
            return;
        }
    }

    milestone = Milestone.Bin;

    if (app.application?.isAvail([milestone])) {
        try {
            yield startCompileWorker(startCompile(id));
            do {
                yield delay(500);
                app = yield select(GetApplicationState);
            } while (!app.application?.isSuccess([milestone]) && !app.application?.isError([milestone]))
        } catch (err: any) {
            yield put(setPhaseStatus(getPhase(milestone), { timedout: true }));
            return;
        }
    }

    milestone = Milestone.Db;

    if (app.application?.isAvail([milestone])) {
        try {
            yield startInitWorker(startInit(id));
            do {
                yield delay(500);
                app = yield select(GetApplicationState);
            } while (!app.application?.isSuccess([milestone]) && !app.application?.isError([milestone]))
        } catch (err: any) {
            yield put(setPhaseStatus(getPhase(milestone), { timedout: true }));
            return;
        }
    }

    milestone = Milestone.Backend;

    if (app.application?.isAvail([milestone])) {
        try {
            yield startBuildBackendWorker(startBuildBackend(id));
            do {
                yield delay(500);
                app = yield select(GetApplicationState);
            } while (!app.application?.isSuccess([milestone]) && !app.application?.isError([milestone]))
        } catch (err: any) {
            yield put(setPhaseStatus(getPhase(milestone), { timedout: true }));
            return;
        }
    }

    milestone = Milestone.Running;

    if (app.application?.isAvail([milestone])) {
        try {
            yield startLaunchWorker(startLaunch(id, app.application!.appPath))
            do {
                yield delay(500);
                app = yield select(GetApplicationState);
            } while (!app.application?.isSuccess([milestone]) && !app.application?.isError([milestone]))
        } catch (err: any) {
            yield put(setPhaseStatus(getPhase(milestone), { timedout: true }));
            return;
        }
    }

    // yield put(endBuild());
}


// Launch = Start + Ping
function* startLaunchWorker(payload: StartLaunchAction) {
    try {
        yield startStart_InternalWorker(startStart(payload.id));
        yield startFetchWorker(startFetchApp(payload.id));
    } catch (err) {
        yield put(setPhaseStatus(AppPhase.Start, { timedout: true }));
        return
    }
}

// Create -----------------------------------
function* startCreateWorker(payload: StartCreateAction) {
    try {
        yield startCreate_InternalWorker(payload)
        yield put(startFetchApp(payload.id));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Create, { timedout: true }));
    }
}
// Create Internal Worker
function* startCreate_InternalWorker(payload: StartCreateAction) {
    let base: string = yield select(GetBaseUrl);
    const { id } = payload;
    const url = `${base}/api/app/${id}/create`;

    yield put(optimisticStartAction(Milestone.Src, moment()));
    let response: Response<any> = yield call(httpPost, url, {});

    if (![200].includes(response.status)) {
        throw 'Could not create';
    }
}

// Compile -----------------------------------
function* startCompileWorker(payload: StartCompileAction) {
    try {
        yield startCompile_InternalWorker(payload);
        yield put(startFetchApp(payload.id));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Compile, { timedout: true }));
    }
}
// Compile Internal Worker
function* startCompile_InternalWorker(payload: StartCompileAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload
    const url = `${base}/api/app/${id}/compile`

    yield put(optimisticStartAction(Milestone.Bin, moment()));
    let response: Response<any> = yield call(httpPost, url, {})

    if (![200].includes(response.status)) {
        throw 'Could not compile'
    }
}

// Init -----------------------------------
function* startInitWorker(payload: StartInitAction) {
    try {
        yield startInit_InternalWorker(payload);
        yield put(startFetchApp(payload.id));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Init, { timedout: true }));
    }
}
// Init Internal Worker
function* startInit_InternalWorker(payload: StartInitAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload
    const url = `${base}/api/app/${id}/init`

    yield put(optimisticStartAction(Milestone.Db, moment()));
    let response: Response<any> = yield call(httpPost, url, {});

    if (![200].includes(response.status)) {
        throw 'Could not create database'
    }
}

// Backend -----------------------------------
function* startBuildBackendWorker(payload: StartBuildBackendAction) {
    try {
        yield startBuildBackend_InternalWorker(payload);
        yield put(startFetchApp(payload.id));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Backend, { timedout: true }));
    }
}
// Backend Internal Worker
function* startBuildBackend_InternalWorker(payload: StartBuildBackendAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload
    const url = `${base}/api/app/${id}/backend`

    yield put(optimisticStartAction(Milestone.Backend, moment()));
    let response: Response<any> = yield call(httpPost, url, {});

    if (![200].includes(response.status)) {
        throw 'Could not build binaries';
    }
}

// Start -----------------------------------
function* startStartWorker(payload: StartStartAction) {
    try {
        yield startStart_InternalWorker(payload);
        yield put(startFetchApp(payload.id));
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Start, { timedout: true }));
    }
}
// Start Internal Worker
function* startStart_InternalWorker(payload: StartStartAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload
    const url = `${base}/api/app/${id}/run`

    yield put(optimisticStartAction(Milestone.Running, moment()));
    let response: Response<any> = yield call(httpPost, url, {});

    if (![200].includes(response.status)) {
        throw 'Could not start application';
    }
}

// Stop
function* startStopWorker(payload: StartStopAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload

    const url = `${base}/api/app/${id}/stop`

    try {
        yield put(optimisticStartAction(Milestone.Stopped, moment()));
        let response: Response<any> = yield call(httpPost, url, {});

        if (![200].includes(response.status)) {
            throw 'Could not stop application';
        }

        yield put(startFetchApp(id))
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Stop, { timedout: true }));
    }
}

// Ping until success or timeout
function* startPingingWorker(payload: StartPingingAction) {
    console.log('Saga: START_PINGING');
    const { name, timeout } = payload;
    yield put(doStartPinging());
    if (pinger !== null && pinger.isRunning()) {
        pinger.cancel();
    }
    pinger = yield spawn(getPingRace(name, timeout));
}

// Ping until failure
function* startHeartbeatWorker(payload: HeartbeatStartAction) {
    if (pinger !== null && pinger.isRunning()) {
        pinger.cancel()
    }
    pinger = yield spawn(getHearbeatPinger(payload.name))
}

function* stopHeartbeatWorker(payload: HeartbeatStopAction) {
    if (pinger !== null && pinger.isRunning()) {
        pinger.cancel()
    }
}

const getPingRace = (name: string, pingTimeout: number) => function* runRace() {
    try {
        const { ping, timeout } = yield race({
            ping: call(getLaunchPinger(name)),
            timeout: delay(pingTimeout)
        })

        if (ping) {
            yield put(successPing())
        } else if (timeout) {
            yield put(pingingTimeout())
        } else {
            throw new Error('Ping error');
        }
    } catch (err) {
        // TODO: hanlde ping error
    } finally {
        const aborted: boolean = yield cancelled();
        if (aborted) {
            // TODO: implement ping interruption
        }
    }
}

// Continuous pinging task until success or timeout
const getLaunchPinger = (name: string) => function* runPinger() {
    let base: string = yield select(GetBaseUrl)
    const url = `${base}/app/${name}`

    while (true) {
        let response: Response<string>
        try {
            response = yield call(httpGet, url, null, 'text/html')
        } catch (e) {
            yield put(failPing())
            return false;
        }

        if ([200, 304].includes(response!.status)) {
            return true;
        } else {
            yield put(failPing())
            yield delay(PING_INTERVAL);
        }
    }
}

// Continuous pinging task until failure
const getHearbeatPinger = (name: string) => function* runPinger() {
    let base: string = yield select(GetBaseUrl)
    const url = `${base}/app/${name}`

    while (true) {
        let response: Response<string> = yield call(httpGet, url, null, 'text/html')

        if ([200, 304].includes(response.status)) {
            yield put(successHeartbeat())
            yield delay(PING_HEARTBEAT_INTERVAL)
        } else {
            yield put(failureHeartbeat())
            break;
        }
    }
}

// Destroy
function* startDestroyWorker(payload: StartDestroyAction) {
    let base: string = yield select(GetBaseUrl)
    const { id } = payload

    const url = `${base}/api/app/${id}/destroy`

    try {
        yield put(optimisticStartAction(Milestone.Erased, moment()));
        let response: Response<any> = yield call(httpPost, url, {})
        // if (![204, 404].includes(response.status)) {
        //     throw 'Could not destroy'
        // }
        // yield put(successDestroy())
        yield put(startFetchApp(id))
    } catch (err: any) {
        yield put(setPhaseStatus(AppPhase.Destroy, { timedout: true }));
    }
}

export default function* StartAppManagementSagas() {
    yield all([fork(startWatchers)])
}
